import React, { useEffect, useMemo, useState } from 'react'
import {
    useDisclosure,
    Modal,
    ModalOverlay,
    ModalContent,
    ModalHeader,
    ModalBody,
    ModalFooter,
    Button,
    Box,
    Text,
    ModalCloseButton,
    Tabs,
    TabList,
    Tab,
    TabPanels,
    TabPanel,
    Spinner,
    Center,
} from '@chakra-ui/react'
import {
    ChatMessageFieldsFragment,
    NotificationLogMessageFieldsFragment,
    useNotificationTemplateLazyQuery,
} from '~graphql/generated/graphql'
import { useClinic } from '~contexts/clinic-context'
import {
    resolvePostcardHtml,
    injectPostcardMergeVars,
    DEFAULT_PRIMARY_COLOR,
    DEFAULT_SECONDARY_COLOR,
} from '~utils/postcard-templates'
import {
    POSTCARD_PREVIEW_HEIGHT_PX,
    POSTCARD_PREVIEW_WIDTH_PX,
} from '~utils/postcard-print-specs'
import {
    NotificationPreviewRef,
    resolveNotificationPreviewContent,
} from './resolve-preview-content'

/**
 * Strips any merge tags that weren't substituted (e.g. `{{repeated_message}}`
 * for an appointment reminder, where the variable was never produced).
 * Mirrors what gets printed: Lob substitutes empty for variables that aren't
 * in the merge vars map, but we want to be explicit here for the preview.
 */
function stripUnresolvedMergeTags(html: string): string {
    return html.replace(/\{\{\s*[a-zA-Z_][a-zA-Z0-9_]*\s*\}\}/g, '')
}

const POSTCARD_WIDTH = POSTCARD_PREVIEW_WIDTH_PX
const POSTCARD_HEIGHT = POSTCARD_PREVIEW_HEIGHT_PX
const POSTCARD_ASPECT = (POSTCARD_HEIGHT / POSTCARD_WIDTH) * 100

function PostcardPreviewIframe({ html }: { html: string }) {
    const containerRef = React.useRef<HTMLDivElement>(null)
    const [scale, setScale] = useState(0.5)

    React.useEffect(() => {
        if (!containerRef.current) return

        const observer = new ResizeObserver(entries => {
            const width = entries[0]?.contentRect.width
            if (width) setScale(width / POSTCARD_WIDTH)
        })
        observer.observe(containerRef.current)
        return () => observer.disconnect()
    }, [])

    return (
        <Box
            ref={containerRef}
            position="relative"
            w="100%"
            pb={`${POSTCARD_ASPECT}%`}
            borderRadius="lg"
            overflow="hidden"
            bg="gray.50"
            borderWidth={1}
            borderColor="gray.200"
        >
            <iframe
                srcDoc={html}
                title="Postcard preview"
                sandbox="allow-same-origin"
                style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: `${POSTCARD_WIDTH}px`,
                    height: `${POSTCARD_HEIGHT}px`,
                    transform: `scale(${scale})`,
                    transformOrigin: 'top left',
                    border: 'none',
                    pointerEvents: 'none',
                }}
            />
        </Box>
    )
}

interface PostcardPreviewButtonProps {
    message: ChatMessageFieldsFragment | NotificationLogMessageFieldsFragment
    notification: NotificationPreviewRef
}

export default function PostcardPreviewButton({
    message,
    notification,
}: PostcardPreviewButtonProps) {
    const {
        isOpen: isPreviewOpen,
        onOpen: onPreviewOpen,
        onClose: onPreviewClose,
    } = useDisclosure()
    const [previewSide, setPreviewSide] = useState<0 | 1>(0)
    const [resolvedNotification, setResolvedNotification] =
        useState(notification)

    const { clinic } = useClinic()
    const [fetchTemplate, { loading: loadingTemplate }] =
        useNotificationTemplateLazyQuery()

    useEffect(() => {
        setResolvedNotification(notification)
    }, [notification])

    useEffect(() => {
        if (!isPreviewOpen) return

        const postcardKey =
            notification.postcard_template || message.postcard_template
        const alreadyResolvable = !!resolvePostcardHtml(
            postcardKey,
            notification.postcard_front_html,
            notification.postcard_back_html,
        )
        if (alreadyResolvable) return

        void fetchTemplate({
            variables: {
                notificationTemplateId: notification.id,
            },
        }).then(result => {
            const full = result.data?.notificationTemplate
            if (!full) return
            setResolvedNotification(
                resolveNotificationPreviewContent(notification, [full]),
            )
        })
    }, [
        isPreviewOpen,
        notification,
        message.postcard_template,
        fetchTemplate,
    ])

    const postcardKey =
        resolvedNotification.postcard_template || message.postcard_template

    const templates = resolvePostcardHtml(
        postcardKey,
        resolvedNotification.postcard_front_html,
        resolvedNotification.postcard_back_html,
    )

    // Mirror the server's `buildPostcardInput` defaults so the preview
    // matches what gets printed even when the original notification didn't
    // produce certain merge variables. The actual QR code sent to Lob has a
    // tracking ID we don't store, so we regenerate one pointing to the same
    // booking page just for the preview.
    const mergeVars = useMemo<Record<string, string>>(() => {
        const stored = (message.template_data ?? {}) as Record<string, string>
        const bookingUrl = `https://portal.chckup.app/book/${clinic?.slug ?? ''}`
        return {
            repeated_message: '',
            qr_code_url: `https://api.qrserver.com/v1/create-qr-code/?size=600x600&data=${encodeURIComponent(bookingUrl)}`,
            primary_color:
                clinic?.settings?.primary_color || DEFAULT_PRIMARY_COLOR,
            secondary_color:
                clinic?.settings?.accent_color || DEFAULT_SECONDARY_COLOR,
            clinic_logo: clinic?.avatar_url ?? '',
            ...stored,
            // Built-in back templates read `repeated_message_back`; mirror the
            // stored (uncapped) list so the back preview isn't blank. The real
            // send caps each side independently server-side.
            repeated_message_back: stored.repeated_message ?? '',
        }
    }, [message.template_data, clinic])

    const frontHtml = useMemo(
        () =>
            templates
                ? stripUnresolvedMergeTags(
                      injectPostcardMergeVars(templates.front, mergeVars),
                  )
                : '',
        [templates, mergeVars],
    )
    const backHtml = useMemo(
        () =>
            templates
                ? stripUnresolvedMergeTags(
                      injectPostcardMergeVars(templates.back, mergeVars),
                  )
                : '',
        [templates, mergeVars],
    )

    return (
        <React.Fragment>
            <Text
                fontWeight="semibold"
                textDecor="underline"
                cursor="pointer"
                onClick={onPreviewOpen}
            >
                {resolvedNotification.name || 'Unnamed Notification'}
            </Text>

            <Modal
                isOpen={isPreviewOpen}
                onClose={onPreviewClose}
                size="2xl"
            >
                <ModalOverlay />
                <ModalContent w="100%" maxW="800px">
                    <ModalCloseButton />
                    <ModalHeader>Postcard Preview</ModalHeader>
                    <ModalBody>
                        {loadingTemplate && !templates ? (
                            <Center py={12}>
                                <Spinner />
                            </Center>
                        ) : (
                            <Tabs
                                index={previewSide}
                                onChange={i => setPreviewSide(i as 0 | 1)}
                                variant="soft-rounded"
                                colorScheme="brand"
                                size="sm"
                            >
                                <TabList mb={3}>
                                    <Tab>Front</Tab>
                                    <Tab>Back</Tab>
                                </TabList>
                                <TabPanels>
                                    <TabPanel p={0}>
                                        {isPreviewOpen && frontHtml && (
                                            <PostcardPreviewIframe
                                                html={frontHtml}
                                            />
                                        )}
                                    </TabPanel>
                                    <TabPanel p={0}>
                                        {isPreviewOpen && backHtml && (
                                            <PostcardPreviewIframe
                                                html={backHtml}
                                            />
                                        )}
                                    </TabPanel>
                                </TabPanels>
                            </Tabs>
                        )}
                    </ModalBody>
                    <ModalFooter>
                        <Button onClick={onPreviewClose}>Close</Button>
                    </ModalFooter>
                </ModalContent>
            </Modal>
        </React.Fragment>
    )
}
